import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CollectionHero from '../components/CollectionHero';
import ProductCard from '../components/ProductCard';
import ProductFilters from '../components/ProductFilters';
import { StoreEmpty, StoreError, StoreLoading } from '../components/StoreState';
import { useProducts } from '../hooks/useProducts';
import {
  fetchCollectionHeaders,
  getCollectionHeader,
  resolveCollectionHeaderEntry,
  SHOP_HEADER_KEY,
} from '../lib/collectionHeaders';
import { fetchStorefrontCategories, slugToCategory } from '../utils/category';
import { parseFilterParams, productMatchesFilters } from '../utils/filters';
import { getProductImageUrl } from '../utils/imageUrl';
import { getSportByName, productBelongsToSport } from '../utils/sports';
import './Shop.css';

const SPORT_TONES = {
  'formula-series': '#1c1c1c',
  football: '#1a2420',
  basketball: '#241c18',
  baseball: '#1a1f24',
  volleyball: '#222018',
  cricket: '#18241c',
  rugby: '#241818',
};

function Shop() {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim().toLowerCase();
  const filters = useMemo(() => parseFilterParams(searchParams), [searchParams]);
  const { products, loading, error } = useProducts();
  const [categories, setCategories] = useState([]);
  const [collectionHeaders, setCollectionHeaders] = useState({});

  useEffect(() => {
    fetchStorefrontCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCollectionHeaders()
      .then(headers => {
        if (!cancelled) setCollectionHeaders(headers);
      })
      .catch(() => {
        if (!cancelled) setCollectionHeaders({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryNames = categories.map(category => category.name);
  const activeCategory = slugToCategory(categorySlug, categoryNames);
  const activeSport = useMemo(
    () => getSportByName(activeCategory),
    [activeCategory]
  );

  const filteredProducts = useMemo(() => {
    let result = products.filter(product => product.show_in_category !== false);

    if (activeSport) {
      result = result.filter(product => productBelongsToSport(product, activeSport));
    } else if (activeCategory) {
      result = result.filter(product => {
        const sport = (product.sport || product.category || '').toLowerCase();
        return sport === activeCategory.toLowerCase();
      });
    }

    if (query) {
      result = result.filter(product => {
        const haystack = [
          product.name,
          product.brand,
          product.team,
          product.league,
          product.sport,
          product.category,
          product.apparel_type,
          product.gender,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    return result.filter(product => productMatchesFilters(product, filters));
  }, [products, activeCategory, activeSport, query, filters]);

  const pageTitle = query
    ? `Results for “${searchParams.get('q').trim()}”`
    : activeCategory
      ? activeCategory
      : 'Shop';

  const header = useMemo(() => {
    if (activeSport) {
      const sportHeader = resolveCollectionHeaderEntry(collectionHeaders, {
        sportSlug: activeSport.slug,
      });
      if (sportHeader) {
        return { imageUrl: sportHeader.image_url, crop: sportHeader.header_crop };
      }
    }
    if (!activeCategory && !query) {
      const shopHeader = getCollectionHeader(collectionHeaders, SHOP_HEADER_KEY);
      if (shopHeader) {
        return { imageUrl: shopHeader.image_url, crop: shopHeader.header_crop };
      }
    }
    const first = filteredProducts.find(product => getProductImageUrl(product));
    const fallback = first ? getProductImageUrl(first) : null;
    return fallback ? { imageUrl: fallback } : { imageUrl: null };
  }, [activeSport, activeCategory, query, collectionHeaders, filteredProducts]);

  const showHero = Boolean(header.imageUrl) || !query;

  const friendlyError =
    error && /supabase|sql|\.env/i.test(error)
      ? 'The shop is temporarily unavailable. Please try again shortly.'
      : error;

  return (
    <main className="shop-page">
      {showHero ? (
        <CollectionHero
          title={pageTitle}
          imageUrl={header.imageUrl}
          crop={header.crop}
          tone={
            (activeSport && SPORT_TONES[activeSport.slug]) || '#1a1a1a'
          }
        />
      ) : (
        <div className="shop-inner shop-inner--plain">
          <header className="shop-header">
            <h1 className="store-title">{pageTitle}</h1>
          </header>
        </div>
      )}

      <div className="shop-inner">
        <div className="shop-layout">
          <ProductFilters />

          <div className="shop-results">
            {friendlyError && <StoreError message={friendlyError} />}
            {!loading && !error && categorySlug && !activeCategory && (
              <StoreEmpty
                eyebrow="Collection"
                title="Sport not found"
                lede="Choose a sport from the index above."
                actionLabel="Browse all"
                actionTo="/shop"
              />
            )}
            {!loading && !error && !(categorySlug && !activeCategory) && filteredProducts.length === 0 && (
              <StoreEmpty
                eyebrow="Collection"
                title="No pieces match"
                lede="Try clearing filters or searching a different kit."
                actionLabel="Clear filters"
                actionTo="/shop"
              />
            )}

            {loading && <StoreLoading label="Loading the collection…" skeletons={6} />}

            {!loading && filteredProducts.length > 0 && (
              <div className="product-card-grid">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id || product.name} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Shop;
